import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DownloadSimple, X, DeviceMobile } from '@phosphor-icons/react';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show the prompt after a delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-24 left-4 right-4 sm:left-auto sm:right-24 sm:w-80 z-[9998] bg-white rounded-2xl shadow-2xl border border-[#E5DCD0] overflow-hidden"
        data-testid="install-prompt"
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-[#B46C4A] rounded-xl flex items-center justify-center flex-shrink-0">
              <DeviceMobile size={24} weight="fill" className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-['Outfit'] font-semibold text-[#2A2825]">Install Safe Space</h3>
              <p className="text-sm text-[#5C564F] mt-1">
                Add to your home screen for quick access anytime you need support.
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="text-[#5C564F] hover:text-[#2A2825] p-1"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleDismiss}
              className="flex-1 py-2 text-sm font-medium text-[#5C564F] hover:text-[#2A2825] transition-colors"
            >
              Not now
            </button>
            <button
              onClick={handleInstall}
              className="flex-1 bg-[#B46C4A] hover:bg-[#9B5C3E] text-white py-2 rounded-full text-sm font-medium flex items-center justify-center gap-2 transition-colors"
              data-testid="install-btn"
            >
              <DownloadSimple size={18} weight="bold" />
              Install
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPrompt;
